import React from "react";
import ReactPaginate from "react-paginate";
import { Eye } from "lucide-react";

export default function ContactsTable({
    records,
    filteredCount,
    absoluteTotal,
    sortField,
    sortOrder,
    onSort,
    formatDate,
    onOpenDetails,
    onOpenUpdate,
    onOpenDelete,
    onOpenLinkedContext,
    onAddSingleToQueue,
    addToQueueDisabled = false,
    pageCount,
    currentPage,
    onPageChange,
    recordsPerPage,
    actionHidden,
}) {
    const start = filteredCount === 0 ? 0 : currentPage * recordsPerPage + 1;
    const end =
        filteredCount === 0
            ? 0
            : Math.min(filteredCount, (currentPage + 1) * recordsPerPage);

    const sortIndicator = (field) =>
        sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "";

    const show = (v) => (v && String(v).trim() !== "" ? v : "N/A");
    const showSource = (v) => (v && String(v).trim() !== "" ? String(v) : "unknown");
    const sourceBadgeClasses = (source) => {
        const s = showSource(source).toLowerCase();
        if (s === "ai_research") return "bg-indigo-50 text-indigo-700 ring-indigo-200";
        if (s === "google_search") return "bg-sky-50 text-sky-700 ring-sky-200";
        if (s === "email_scraping") return "bg-amber-50 text-amber-800 ring-amber-200";
        if (s === "user_upload") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
        return "bg-slate-50 text-slate-700 ring-slate-200";
    };

    const columns = [
        ["Name", "contact_name"],
        ["Profile", "profile_url"],
        ["Company", "company_name"],
        ["Website", "company_website"],
        ["AI Position", "ai_position"],
        ["Follower", "follower_count"],
        ["Source", "source"],
        ["Created At", "created_at"],
    ];

    return (
        <div className="w-full max-w-full">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                {/* Header */}
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Contacts</div>
                        <div className="text-sm text-slate-600">
                            {filteredCount > 0
                                ? `Showing ${start}-${end} of ${filteredCount} contact${filteredCount === 1 ? "" : "s"}${absoluteTotal !== filteredCount ? ` (out of ${absoluteTotal})` : ""
                                }`
                                : `Showing 0 of ${absoluteTotal} contacts`}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                            Page {currentPage + 1} / {pageCount || 1}
                        </span>
                        {!actionHidden ? (
                            <span className="hidden sm:inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                                Click a row for details
                            </span>
                        ) : null}
                    </div>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full table-fixed divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {columns.map(([label, field]) => {
                                    const active = sortField === field;
                                    return (
                                        <th
                                            key={field}
                                            onClick={() => onSort(field)}
                                            className={[
                                                "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700",
                                                "cursor-pointer select-none hover:bg-slate-100",
                                                active ? "bg-slate-100" : "",
                                            ].join(" ")}
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {label}
                                                {sortIndicator(field) && (
                                                    <span className="text-slate-900">{sortIndicator(field)}</span>
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}

                                {!actionHidden && (
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-200 bg-white">
                            {records.length > 0 ? (
                                records.map((c) => (
                                    <tr
                                        key={c.contact_id}
                                        className="group cursor-pointer hover:bg-slate-50"
                                        onClick={() => onOpenDetails(c)}
                                    >
                                        <td className="px-4 py-4 text-sm font-medium text-slate-900 max-w-[220px] truncate">
                                            {show(c.contact_name)}
                                        </td>

                                        <td className="px-4 py-4 text-sm max-w-[240px] truncate">
                                            {c.profile_url ? (
                                                <a
                                                    href={
                                                        c.profile_url.startsWith("http")
                                                            ? c.profile_url
                                                            : `https://${c.profile_url}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                >
                                                    {c.profile_url}
                                                </a>
                                            ) : (
                                                <span className="text-slate-500">N/A</span>
                                            )}
                                        </td>

                                        <td className="px-4 py-4 text-sm text-slate-900 max-w-[220px] truncate">
                                            {show(c.company_name)}
                                        </td>

                                        <td className="px-4 py-4 text-sm max-w-[240px] truncate">
                                            {c.company_website ? (
                                                <a
                                                    href={
                                                        c.company_website.startsWith("http")
                                                            ? c.company_website
                                                            : `https://${c.company_website}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                >
                                                    {c.company_website}
                                                </a>
                                            ) : (
                                                <span className="text-slate-500">N/A</span>
                                            )}
                                        </td>

                                        <td className="px-4 py-4 text-sm text-slate-700 max-w-[220px] truncate">
                                            {show(c.ai_position)}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {show(c.follower_count)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            <span className={["inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1", sourceBadgeClasses(c.source)].join(" ")}>
                                                {showSource(c.source)}
                                            </span>
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {formatDate(c.created_at)}
                                        </td>

                                        {!actionHidden && (
                                            <td className="px-4 py-4 whitespace-nowrap text-left">
                                                <div className="flex justify-start gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenLinkedContext(c);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                        title="Related Company"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenUpdate(c);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                        title="Update Contact"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-4 w-4"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-7.293 7.293A1 1 0 018.001 14H5a1 1 0 01-1-1v-3a1 1 0 01.293-.707l7.293-7.293zM14 6L11 9l-2-2 3-3 2 2z" />
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenDelete(c);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                        title="Delete Contact"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-4 w-4"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M9 2a1 1 0 00-1 1v1H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-4V3a1 1 0 00-1-1H9zm1 2H9v1h2V4zm0 2H9v6h2V6z"
                                                                clipRule="evenodd"
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 2a1 1 0 011 1v1h4a1 1 0 010 2v10a2 2 0 01-2 2H5a1 1 0 01-1-1V6a1 1 0 010-2h4V3a1 1 0 011-1z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAddSingleToQueue(c);
                                                        }}
                                                        disabled={addToQueueDisabled}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Add to Connect Queue"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-4 w-4"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={actionHidden ? columns.length : columns.length + 1}
                                        className="px-6 py-10 text-center text-sm text-slate-500"
                                    >
                                        No contacts found for the current filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="block md:hidden space-y-3">
                    {records.map((c) => (
                        <div
                            key={c.contact_id}
                            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50 cursor-pointer"
                            onClick={() => onOpenDetails(c)}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm text-slate-700">
                                        <span className="font-semibold text-slate-900">Name:</span>{" "}
                                        {show(c.contact_name)}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-700">
                                        <span className="font-semibold text-slate-900">Company:</span>{" "}
                                        {show(c.company_name)}
                                    </p>
                                </div>

                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                                <div className="col-span-2">
                                    <span className="font-semibold text-slate-900">Profile:</span>{" "}
                                    {c.profile_url ? (
                                        <a
                                            href={c.profile_url.startsWith("http") ? c.profile_url : `https://${c.profile_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="break-all text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                        >
                                            {c.profile_url}
                                        </a>
                                    ) : (
                                        "N/A"
                                    )}
                                </div>

                                <div className="col-span-2">
                                    <span className="font-semibold text-slate-900">Website:</span>{" "}
                                    {c.company_website ? (
                                        <a
                                            href={c.company_website.startsWith("http") ? c.company_website : `https://${c.company_website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="break-all text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                        >
                                            {c.company_website}
                                        </a>
                                    ) : (
                                        "N/A"
                                    )}
                                </div>

                                <div>
                                    <span className="font-semibold text-slate-900">AI Position:</span>{" "}
                                    {show(c.ai_position)}
                                </div>
                                <div>
                                    <span className="font-semibold text-slate-900">Follower:</span>{" "}
                                    {show(c.follower_count)}
                                </div>
                                <div>
                                    <span className="font-semibold text-slate-900">Source:</span>{" "}
                                    <span className={["ml-1 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1", sourceBadgeClasses(c.source)].join(" ")}>
                                        {showSource(c.source)}
                                    </span>
                                </div>
                                <div className="col-span-2">
                                    <span className="font-semibold text-slate-900">Created:</span>{" "}
                                    {formatDate(c.created_at)}
                                </div>
                            </div>

                            {!actionHidden && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenLinkedContext(c);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                        title="Related Company"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenUpdate(c);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                    >
                                        Update
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenDelete(c);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                    >
                                        Delete
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddSingleToQueue(c);
                                        }}
                                        disabled={addToQueueDisabled}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                    >
                                        Add to queue
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                <div className="mt-6">
                    <ReactPaginate
                        previousLabel={
                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                ← Previous
                            </span>
                        }
                        nextLabel={
                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                Next →
                            </span>
                        }
                        pageCount={pageCount}
                        forcePage={currentPage}
                        onPageChange={({ selected }) => onPageChange(selected)}
                        containerClassName="flex flex-wrap items-center justify-center gap-2"
                        pageClassName="block"
                        pageLinkClassName="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
                        activeLinkClassName="!bg-slate-900 !text-white !border-slate-900"
                        disabledClassName="opacity-50 cursor-not-allowed"
                        breakClassName="px-2 py-2 text-slate-400"
                        renderOnZeroPageCount={null}
                    />
                </div>
            </div>
        </div>
    );
}
